import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCartIcon, HeartIcon, ShareIcon, CheckIcon, StarIcon, TagIcon, CalendarIcon, UserIcon, EyeIcon, PlayIcon, InfoIcon, PencilIcon, LayoutDashboardIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { VideoPlayer } from '../components/video/VideoPlayer';
import { ResponsiveVideoPlayer } from '../components/video/ResponsiveVideoPlayer';
import { useProduct } from '../hooks/useFirestore';
import { useAuth } from '../components/auth/AuthProvider';
import { useCart } from '../components/cart/CartContext';
import { useFavorites } from '../hooks/useFavorites';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { motion } from 'framer-motion';
// Move helper function outside of component
const getVideoPlayerStyles = (isVertical = false) => {
  if (isVertical) {
    return {
      maxWidth: '100%',
      margin: '0 auto'
    };
  }
  return {
    width: '100%',
    height: '100%'
  };
};
export const ProductDetailPage = () => {
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const {
    product,
    loading,
    error
  } = useProduct(productId);
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    addItem,
    openCart
  } = useCart();
  const navigate = useNavigate();
  const {
    isFavorited,
    toggleFavorite
  } = useFavorites();
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState<'idle' | 'adding' | 'added' | 'error'>('idle');
  const [sellerStoreOnline, setSellerStoreOnline] = useState<boolean | null>(null);
  const [sellerCheckLoading, setSellerCheckLoading] = useState(true);
  // Set the selected image once the product is loaded
  useEffect(() => {
    if (product && product.image) {
      setSelectedImage(product.image);
    }
  }, [product]);
  // Fetch seller's storeOnline status
  useEffect(() => {
    if (!product?.sellerId) {
      setSellerCheckLoading(false);
      return;
    }
    // Skip check if the current user is the owner
    if (user?.id && user.id === product.sellerId) {
      setSellerStoreOnline(true);
      setSellerCheckLoading(false);
      return;
    }
    const checkSellerStore = async () => {
      try {
        const sellerDoc = await getDoc(doc(db, 'users', product.sellerId!));
        if (sellerDoc.exists()) {
          const data = sellerDoc.data();
          setSellerStoreOnline(data.storeOnline !== undefined ? data.storeOnline : true);
        } else {
          setSellerStoreOnline(true);
        }
      } catch {
        // If we can't check, default to showing the product
        setSellerStoreOnline(true);
      } finally {
        setSellerCheckLoading(false);
      }
    };
    checkSellerStore();
  }, [product?.sellerId, user?.id]);
  // Handle add to cart action
  const handleAddToCart = () => {
    if (!isAuthenticated) {
      navigate('/sell-with-us', {
        state: {
          openAuth: true,
          redirectTo: `/product/${productId}`
        }
      });
      return;
    }
    if (!product) return;
    setPurchaseStatus('adding');
    try {
      addItem({
        id: product.id || '',
        title: product.title,
        price: product.price,
        image: product.image || ''
      });
      setPurchaseStatus('added');
      // Open cart sidebar to show the added item
      openCart();
      // Reset status after 3 seconds
      setTimeout(() => {
        setPurchaseStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('[AddToCart] Error adding to cart:', error);
      setPurchaseStatus('error');
      // Reset status after 3 seconds
      setTimeout(() => {
        setPurchaseStatus('idle');
      }, 3000);
    }
  };
  // Toggle wishlist/favorites status
  const toggleWishlist = () => {
    if (productId) {
      toggleFavorite(productId);
    }
  };
  const isInWishlist = productId ? isFavorited(productId) : false;
  // Share the product
  const shareProduct = () => {
    setShowShareOptions(!showShareOptions);
  };
  // Determine if product is a video
  const isVideoProduct = product?.category === 'Short-form Video';
  const isVerticalVideo = product?.videoMetadata?.isVertical || false;
  // Detect if the logged-in user is the product owner
  const isOwner = !!(user?.id && product?.sellerId && user.id === product.sellerId);
  // Loading state
  if (loading) {
    return <>
        <Navbar scrolled={true} data-id="element-5343" />
        <div className="container mx-auto px-4 py-24 flex justify-center items-center min-h-screen" data-id="element-5344">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2DB489]" data-id="element-5345"></div>
        </div>
        <Footer data-id="element-5346" />
      </>;
  }
  // Error state
  if (error || !product) {
    return <>
        <Navbar scrolled={true} data-id="element-5347" />
        <div className="container mx-auto px-4 py-24 flex flex-col justify-center items-center min-h-screen" data-id="element-5348">
          <div className="bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 p-4 rounded-lg mb-4" data-id="element-5349">
            <p data-id="element-5350">Error loading product. Please try again later.</p>
            {error && <p className="text-sm mt-2" data-id="element-5351">{error.message}</p>}
          </div>
          <button onClick={() => navigate('/browse')} className="mt-4 bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-6 py-2 rounded-lg hover:bg-opacity-90 transition-all" data-id="element-5352">
            Browse Products
          </button>
        </div>
        <Footer data-id="element-5353" />
      </>;
  }
  // Store offline: block non-owners
  if (!sellerCheckLoading && sellerStoreOnline === false && !isOwner) {
    return <>
        <Navbar scrolled={true} data-id="element-5354" />
        <div className="container mx-auto px-4 py-24 flex flex-col justify-center items-center min-h-screen" data-id="element-5355">
          <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center max-w-md" data-id="element-5356">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gray-100 dark:bg-[#1A3330] flex items-center justify-center" data-id="element-5357">
              <InfoIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-5358" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-5359">
              This product is currently unavailable
            </h1>
            <p className="text-gray-600 dark:text-[#B0C1BD] mb-8" data-id="element-5360">
              The seller has temporarily taken their store offline. Please check
              back later.
            </p>
            <button onClick={() => navigate('/browse')} className="bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-all" data-id="element-5361">
              Browse Products
            </button>
          </div>
        </div>
        <Footer data-id="element-5362" />
      </>;
  }
  // Show loading while checking seller store status
  if (sellerCheckLoading) {
    return <>
        <Navbar scrolled={true} data-id="element-5363" />
        <div className="container mx-auto px-4 py-24 flex justify-center items-center min-h-screen" data-id="element-5364">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2DB489]" data-id="element-5365"></div>
        </div>
        <Footer data-id="element-5366" />
      </>;
  }
  // Format the product description for display
  const formatDescription = (description: string) => {
    if (!description) return '';
    // If showing full description or description is short, return it as is
    if (showFullDescription || description.length < 300) {
      return description;
    }
    // Otherwise truncate it
    return description.substring(0, 300) + '...';
  };
  return <>
      <Navbar scrolled={true} data-id="element-5367" />
      <main className="container mx-auto px-4 pt-24 pb-12 md:pt-28 md:pb-16 max-w-7xl" data-id="element-5368">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-5369">
          {/* Left Column - Product Media */}
          <div className="space-y-4 w-full max-w-lg mx-auto lg:mx-0" data-id="element-5370">
            {isVideoProduct /* Video Player for video products */ ? <div className="bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm" data-id="element-5371">
                <div className={`relative ${isVerticalVideo ? 'max-w-sm mx-auto' : 'w-full'} overflow-hidden`} data-id="element-5372">
                  <ResponsiveVideoPlayer videoUrl={product.videoUrl || ''} title={product.title} posterImage={product.image} allowDownload={false} autoPlay={false} videoMetadata={product.videoMetadata} data-id="element-5373" />
                </div>
              </div> /* Image Gallery for regular products */ : <>
                <div className="bg-white dark:bg-[#162825] rounded-xl overflow-hidden shadow-sm" data-id="element-5374">
                  <div className="relative aspect-[4/3] overflow-hidden bg-black" data-id="element-5375">
                    <img src={selectedImage} alt={product.title} className="w-full h-full object-cover" data-id="element-5376" />
                  </div>
                </div>
                {/* Thumbnail gallery */}
                {product.previewImages && product.previewImages.length > 0 && <div className="flex space-x-3 overflow-x-auto pb-2" data-id="element-5377">
                    <div className={`w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 ${selectedImage === product.image ? 'border-[#2DB489] dark:border-[#4ED1A0]' : 'border-transparent'}`} onClick={() => setSelectedImage(product.image || '')} data-id="element-5378">
                      <img src={product.image} alt={`${product.title} - main`} className="w-full h-full object-cover" data-id="element-5379" />
                    </div>
                    {product.previewImages.map((img, index) => <div key={index} className={`w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 ${selectedImage === img ? 'border-[#2DB489] dark:border-[#4ED1A0]' : 'border-transparent'}`} onClick={() => setSelectedImage(img)} data-id="element-5380">
                        <img src={img} alt={`${product.title} - preview ${index + 1}`} className="w-full h-full object-cover" data-id="element-5381" />
                      </div>)}
                  </div>}
              </>}
          </div>

          {/* Right Column - Product Info */}
          <div className="space-y-6" data-id="element-5382">
            {/* Product category */}
            <div data-id="element-5383">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400" data-id="element-5384">
                <TagIcon className="w-3 h-3 mr-1" data-id="element-5385" />
                {product.category}
              </span>
            </div>

            {/* Product title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-5386">
              {product.title}
            </h1>

            {/* Seller info and date */}
            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-5387">
              <div className="flex items-center" data-id="element-5388">
                <UserIcon className="w-4 h-4 mr-1" data-id="element-5389" />
                <span data-id="element-5390">By {product.sellerName || 'Unknown Seller'}</span>
              </div>
              <div className="flex items-center" data-id="element-5391">
                <CalendarIcon className="w-4 h-4 mr-1" data-id="element-5392" />
                <span data-id="element-5393">
                  Added{' '}
                  {new Date(product.createdAt || Date.now()).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-baseline" data-id="element-5394">
              <span className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-5395">
                ${product.price}
              </span>
              {product.originalPrice && product.originalPrice > product.price && <span className="ml-2 text-lg text-gray-500 dark:text-[#7A8F8A] line-through" data-id="element-5396">
                    ${product.originalPrice}
                  </span>}
            </div>

            {/* Description */}
            <div data-id="element-5397">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5398">
                Description
              </h2>
              <p className="text-gray-600 dark:text-[#B0C1BD] whitespace-pre-line" data-id="element-5399">
                {formatDescription(product.description)}
              </p>
              {product.description && product.description.length > 300 && <button onClick={() => setShowFullDescription(!showFullDescription)} className="mt-2 text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium hover:underline" data-id="element-5400">
                  {showFullDescription ? 'Show less' : 'Read more'}
                </button>}
            </div>

            {/* Features */}
            {isVideoProduct ? <div data-id="element-5401">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5402">
                  Video Details
                </h2>
                <ul className="space-y-2" data-id="element-5403">
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-5404">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-5405" />
                    <span data-id="element-5406">Video Type: {product.videoType || 'Standard'}</span>
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-5407">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-5408" />
                    <span data-id="element-5409">Commercial Use License Included</span>
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-5410">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-5411" />
                    <span data-id="element-5412">Immediate Download After Purchase</span>
                  </li>
                </ul>
              </div> : <div data-id="element-5413">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-5414">
                  Features
                </h2>
                <ul className="space-y-2" data-id="element-5415">
                  {product.format && <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-5416">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-5417" />
                      <span data-id="element-5418">Format: {product.format}</span>
                    </li>}
                  {product.licenseType && <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-5419">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-5420" />
                      <span data-id="element-5421">License: {product.licenseType}</span>
                    </li>}
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-5422">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-5423" />
                    <span data-id="element-5424">Immediate Download</span>
                  </li>
                </ul>
              </div>}

            {/* Action buttons */}
            {isOwner ? <div className="pt-4 space-y-3" data-id="element-5425">
                {/* Owner quick actions */}
                <div className="flex space-x-3" data-id="element-5426">
                  <button onClick={() => navigate('/seller-dashboard')} className="flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 dark:text-[#B0C1BD] bg-gray-100 dark:bg-[#1A3330] hover:bg-gray-200 dark:hover:bg-[#214541] transition-colors" data-id="element-5427">
                    <LayoutDashboardIcon className="w-4 h-4 mr-2" data-id="element-5428" />
                    Back to Dashboard
                  </button>
                  <button onClick={() => navigate('/seller-dashboard')} className="flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-[#2DB489] dark:bg-[#4ED1A0] hover:opacity-90 transition-opacity" data-id="element-5429">
                    <PencilIcon className="w-4 h-4 mr-2" data-id="element-5430" />
                    Edit Product
                  </button>
                </div>
              </div> : <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4" data-id="element-5431">
                <motion.button onClick={handleAddToCart} className={`flex-1 flex items-center justify-center px-6 py-3 rounded-lg font-medium text-white ${purchaseStatus === 'adding' ? 'bg-gray-400 dark:bg-gray-600' : purchaseStatus === 'added' ? 'bg-green-500 dark:bg-green-600' : 'bg-[#0A3C52] dark:bg-[#0B3C45] hover:bg-opacity-90'}`} disabled={purchaseStatus === 'adding' || purchaseStatus === 'added'} whileHover={purchaseStatus === 'idle' ? {
              scale: 1.02
            } : {}} whileTap={purchaseStatus === 'idle' ? {
              scale: 0.98
            } : {}} data-id="element-5432">
                  {purchaseStatus === 'adding' ? <span className="flex items-center" data-id="element-5433">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-5434">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-5435"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-5436"></path>
                      </svg>
                      Adding...
                    </span> : purchaseStatus === 'added' ? <span className="flex items-center" data-id="element-5437">
                      <CheckIcon className="w-4 h-4 mr-2" data-id="element-5438" />
                      Added to Cart
                    </span> : <span className="flex items-center" data-id="element-5439">
                      <ShoppingCartIcon className="w-4 h-4 mr-2" data-id="element-5440" />
                      Add to Cart
                    </span>}
                </motion.button>
              </div>}

            {/* Secondary actions */}
            <div className="flex space-x-4 pt-2" data-id="element-5441">
              <button onClick={toggleWishlist} className={`flex items-center text-sm ${isInWishlist ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-[#7A8F8A] hover:text-red-500 dark:hover:text-red-400'}`} data-id="element-5442">
                <HeartIcon className={`w-4 h-4 mr-1 ${isInWishlist ? 'fill-current' : ''}`} data-id="element-5443" />
                {isInWishlist ? 'Saved' : 'Save'}
              </button>
              <div className="relative" data-id="element-5444">
                <button onClick={shareProduct} className="flex items-center text-sm text-gray-500 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0]" data-id="element-5445">
                  <ShareIcon className="w-4 h-4 mr-1" data-id="element-5446" />
                  Share
                </button>
                {showShareOptions && <div className="absolute left-0 mt-2 w-48 bg-white dark:bg-[#162825] rounded-lg shadow-lg py-2 z-20 border border-gray-100 dark:border-[#1A3330]" data-id="element-5447">
                    <button className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-100 dark:hover:bg-[#1A3330]" onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  setShowShareOptions(false);
                }} data-id="element-5448">
                      Copy Link
                    </button>
                    {/* Add more share options as needed */}
                  </div>}
              </div>
              {product.demoUrl && <a href={product.demoUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-gray-500 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0]" data-id="element-5449">
                  <EyeIcon className="w-4 h-4 mr-1" data-id="element-5450" />
                  Live Preview
                </a>}
            </div>
          </div>
        </div>

        {/* Additional sections like reviews, related products, etc. would go here */}
      </main>
      <Footer data-id="element-5451" />
    </>;
};